/**
 * frontend/src/pages/Privacy.js
 * 
 * Public privacy policy page.
 * Details how ChangeFox collects, uses, and protects user data in compliance with regulations.
 */
import React from 'react';
import { Container } from 'react-bootstrap';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import BackgroundShapes from '../components/common/BackgroundShapes';
import Footer from '../components/common/Footer';
import SEO from '../components/common/SEO';
import logo from '../assets/logo.png';
import '../styles/landing.css';

const Privacy = () => {
  return (
    <div className="landing-page">
      <SEO
        title="Privacy Policy - ChangeFox"
        description="Learn how ChangeFox protects your privacy and handles your personal information."
        keywords="privacy policy, data protection, GDPR, changefox"
        path="/privacy"
      />
      <BackgroundShapes />

      {/* Navigation */}
      <motion.nav
        className="landing-nav"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Container>
          <div className="nav-content">
            <Link to="/" className="nav-logo">
              <img src={logo} alt="ChangeFox" className="nav-logo-img" />
            </Link>
            <div className="nav-links">
              <Link to="/" className="nav-link">Home</Link>
              <Link to="/login" className="nav-link">Sign In</Link>
            </div>
          </div>
        </Container>
      </motion.nav>

      {/* Content */}
      <section className="legal-section">
        <Container>
          <motion.div
            className="legal-content"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h1 className="legal-title">Privacy Policy</h1>
            <p className="legal-updated">Last updated: January 2026</p>

            <div className="legal-body">
              <h2>1. Introduction</h2>
              <p>
                ChangeFox ("we", "our", or "us") is committed to protecting your privacy. This Privacy Policy
                explains how we collect, use, disclose, and safeguard your information when you use our
                compliance change management platform.
              </p>

              <h2>2. Information We Collect</h2>
              <h3>Personal Information</h3>
              <p>We may collect personal information that you provide directly to us, including:</p>
              <ul>
                <li>Name and email address</li>
                <li>Account credentials</li>
                <li>Company or organization name</li>
                <li>Job title and role</li>
                <li>Communication preferences</li>
              </ul>

              <h3>Usage Information</h3>
              <p>We automatically collect certain information when you use our Service:</p>
              <ul>
                <li>Log data (IP address, browser type, pages visited)</li>
                <li>Device information</li>
                <li>Usage patterns and interactions with the Service</li>
              </ul>

              <h2>3. How We Use Your Information</h2>
              <p>We use the information we collect to:</p>
              <ul>
                <li>Provide and maintain our Service</li>
                <li>Process your change requests and manage your account</li>
                <li>Send you updates and administrative messages</li>
                <li>Respond to your inquiries and support requests</li>
                <li>Improve and optimize our Service</li>
                <li>Ensure security and prevent fraud</li>
              </ul>

              <h2>4. Information Sharing</h2>
              <p>We do not sell your personal information. We may share your information with:</p>
              <ul>
                <li>Service providers who assist in operating our platform</li>
                <li>Legal authorities when required by law</li>
                <li>Business partners with your consent</li>
              </ul>

              <h2>5. Data Security</h2>
              <p>
                We implement appropriate technical and organizational measures to protect your personal
                information against unauthorized access, alteration, disclosure, or destruction. However,
                no method of transmission over the Internet is 100% secure.
              </p>

              <h2>6. Data Retention</h2>
              <p>
                We retain your personal information for as long as necessary to fulfill the purposes
                outlined in this Privacy Policy, unless a longer retention period is required by law.
              </p>

              <h2>7. Your Rights</h2>
              <p>Depending on your location, you may have the right to:</p>
              <ul>
                <li>Access your personal information</li>
                <li>Correct inaccurate data</li>
                <li>Request deletion of your data</li>
                <li>Object to processing of your data</li>
                <li>Request data portability</li>
              </ul>

              <h2>8. Cookies</h2>
              <p>
                We use cookies and similar tracking technologies to track activity on our Service and
                hold certain information. You can instruct your browser to refuse all cookies or to
                indicate when a cookie is being sent.
              </p>

              <h2>9. Third-Party Links</h2>
              <p>
                Our Service may contain links to third-party websites. We are not responsible for the
                privacy practices of these external sites.
              </p>

              <h2>10. Children's Privacy</h2>
              <p>
                Our Service is not intended for children under 13 years of age. We do not knowingly
                collect personal information from children under 13.
              </p>

              <h2>11. Changes to This Policy</h2>
              <p>
                We may update this Privacy Policy from time to time. We will notify you of any changes
                by posting the new Privacy Policy on this page and updating the "Last updated" date.
              </p>

              <h2>12. Contact Us</h2>
              <p>
                If you have questions about this Privacy Policy, please contact us at{' '}
                <a href="mailto:contact@changefox.ai">contact@changefox.ai</a>.
              </p>
            </div>
          </motion.div>
        </Container>
      </section>

      <Footer />
    </div>
  );
};

export default Privacy;
